import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { BottomSheet } from './BottomSheet';
import type { ErrorSpot, MatchPoint, PointEndReason, RallyBucket, ShotType } from '../../../../types';
import type { Side } from '../../../../services/tennis/scoring';

export type PointDetailsUpdate = Pick<
  MatchPoint,
  'end_reason' | 'winning_player_id' | 'shot_type' | 'error_spot' | 'net_approach' | 'rally_bucket'
>;

interface DoublesPlayer {
  id: string;
  firstName: string;
}

interface PointDetailsSheetProps {
  open: boolean;
  isProUser: boolean;
  /** Which side won the point just recorded — colors the header swatch. */
  wonBy: Side | null;
  wonByName: string;
  /** Winning side's score before → after this point, e.g. { from: '30', to: '40' }. */
  scoreDelta: { from: string; to: string } | null;
  /** Doubles + the point just recorded was won by us — shows the "Who won it?" credit row. Never shown for singles. */
  showWinningPlayerPrompt: boolean;
  ourPlayers: DoublesPlayer[];
  /** Only the serving side can win on an ace — the option is hidden otherwise. */
  canBeAce: boolean;
  /** Open with "More detail" already expanded (and no auto-save timer). Driven by the future team point-detail setting. */
  startExpanded?: boolean;
  /** How long the collapsed drawer waits for a tap before saving the point as-is. Null turns auto-save off. */
  autoSaveMs: number | null;
  onSave: (updates: PointDetailsUpdate) => void;
  onSkip: () => void;
  /** Rookie tapped a Pro-only detail chip — surface the upgrade modal without selecting anything. */
  onRequirePro: () => void;
}

const REASONS: Array<{ value: PointEndReason; label: string; short: string; tone: string }> = [
  { value: 'winner', label: 'Winner', short: 'W', tone: 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400' },
  { value: 'ace', label: 'Ace', short: 'A', tone: 'bg-teal-50 text-teal-700 dark:bg-teal-950 dark:text-teal-400' },
  { value: 'forced', label: 'Forced error', short: 'FE', tone: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400' },
  { value: 'unforced', label: 'Unforced error', short: 'UE', tone: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-400' },
];

// No "serve" chip: an ace is logged as an outcome, and double faults come from the Fault button.
const SHOT_TYPES: Array<{ value: ShotType; label: string }> = [
  { value: 'forehand', label: 'Forehand' },
  { value: 'backhand', label: 'Backhand' },
  { value: 'volley', label: 'Volley' },
  { value: 'overhead', label: 'Overhead' },
  { value: 'dropshot', label: 'Drop' },
  { value: 'lob', label: 'Lob' },
];
const ERROR_SPOTS: Array<{ value: ErrorSpot; label: string }> = [
  { value: 'net', label: 'Net' },
  { value: 'long', label: 'Long' },
  { value: 'wide', label: 'Wide' },
];
const RALLY_BUCKETS: RallyBucket[] = ['1-3', '4-6', '7-9', '10+'];

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-[10px] font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1.5">{children}</p>;
}

function Chip({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`min-h-[40px] px-3.5 rounded-full border text-sm font-medium ${
        selected
          ? 'border-primary bg-primary text-white'
          : 'border-gray-200 bg-gray-50 text-gray-700 active:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:active:bg-gray-700'
      }`}
    >
      {label}
    </button>
  );
}

/** Drains over `durationMs`; remount (via `key`) to restart it. */
function TimerBar({ durationMs }: { durationMs: number }) {
  const [drained, setDrained] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDrained(true), 20);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="flex-1 h-1 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
      <div
        className="h-full bg-primary"
        style={{ width: drained ? '0%' : '100%', transition: `width ${durationMs - 20}ms linear` }}
      />
    </div>
  );
}

/**
 * Post-point tag drawer. The point already counted the instant its pad was
 * tapped; this only attaches detail to it.
 *
 * Collapsed (default): tapping an outcome saves and closes in one tap, and if
 * nothing is tapped the drawer saves itself after `autoSaveMs` (when the
 * scorer has auto-save on — see scoringPreferences). Picking a
 * player restarts the timer. Expanding "More detail" stops the timer — Save
 * becomes the way out. Tapping the backdrop saves whatever is selected.
 */
export const PointDetailsSheet: React.FC<PointDetailsSheetProps> = ({
  open,
  isProUser,
  wonBy,
  wonByName,
  scoreDelta,
  showWinningPlayerPrompt,
  ourPlayers,
  canBeAce,
  startExpanded = false,
  autoSaveMs,
  onSave,
  onSkip,
  onRequirePro,
}) => {
  const [expanded, setExpanded] = useState(startExpanded);
  const [endReason, setEndReason] = useState<PointEndReason | null>(null);
  const [winningPlayerId, setWinningPlayerId] = useState<string | null>(null);
  const [shotType, setShotType] = useState<ShotType | null>(null);
  const [errorSpot, setErrorSpot] = useState<ErrorSpot | null>(null);
  const [netApproach, setNetApproach] = useState<boolean | null>(null);
  const [rallyBucket, setRallyBucket] = useState<RallyBucket | null>(null);

  // Fresh state for each new point.
  useEffect(() => {
    if (!open) return;
    setExpanded(startExpanded);
    setEndReason(null);
    setWinningPlayerId(null);
    setShotType(null);
    setErrorSpot(null);
    setNetApproach(null);
    setRallyBucket(null);
  }, [open, startExpanded]);

  const save = useCallback(
    (reason: PointEndReason | null = endReason) => {
      const isError = reason === 'forced' || reason === 'unforced';
      onSave({
        end_reason: reason,
        winning_player_id: winningPlayerId,
        shot_type: shotType,
        error_spot: isError ? errorSpot : null,
        net_approach: netApproach,
        rally_bucket: rallyBucket,
      });
    },
    [endReason, winningPlayerId, shotType, errorSpot, netApproach, rallyBucket, onSave]
  );

  // Auto-save while collapsed. Re-arms when the player pick changes (the only collapsed-mode selection
  // that doesn't itself save). Reads save through a ref so parent re-renders (e.g. background sync)
  // don't silently push the deadline out past what the timer bar shows.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    if (!open || expanded || autoSaveMs === null) return;
    const t = setTimeout(() => saveRef.current(), autoSaveMs);
    return () => clearTimeout(t);
  }, [open, expanded, winningPlayerId, autoSaveMs]);

  const handleProTap = (apply: () => void) => {
    if (!isProUser) {
      onRequirePro();
      return;
    }
    apply();
  };

  const handleReasonTap = (reason: PointEndReason) => {
    if (!expanded) {
      save(reason);
      return;
    }
    setEndReason(endReason === reason ? null : reason);
  };

  const reasons = REASONS.filter((r) => canBeAce || r.value !== 'ace');
  // Without Ace there are three outcomes — one row of stacked tiles instead of a 2×2 grid with a gap.
  const compactReasons = reasons.length === 3;
  const showErrorSpot = endReason === 'forced' || endReason === 'unforced';
  const swatch = wonBy === 'us' ? 'bg-primary' : 'bg-slate-700 dark:bg-slate-400';

  return (
    <BottomSheet open={open} onClose={() => save()}>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="flex items-center gap-2 min-w-0 text-lg font-extrabold text-gray-900 dark:text-gray-50">
            <span className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${swatch}`} aria-hidden />
            <span className="truncate">Point {wonByName}</span>
          </p>
          {scoreDelta && (
            <span className="text-xl font-extrabold text-primary tabular-nums whitespace-nowrap">
              {scoreDelta.from} → {scoreDelta.to}
            </span>
          )}
        </div>

        {!expanded && autoSaveMs !== null && (
          <div className="flex items-center gap-3 text-xs font-semibold text-gray-500 dark:text-gray-400">
            <span>Saves automatically</span>
            <TimerBar key={`${winningPlayerId ?? 'none'}`} durationMs={autoSaveMs} />
          </div>
        )}

        {showWinningPlayerPrompt && ourPlayers.length >= 2 && (
          <div>
            <SectionLabel>Who won it?</SectionLabel>
            <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800">
              {ourPlayers.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  aria-pressed={winningPlayerId === p.id}
                  onClick={() => setWinningPlayerId(winningPlayerId === p.id ? null : p.id)}
                  className={`min-h-[44px] rounded-lg text-[15px] font-bold ${
                    winningPlayerId === p.id ? 'bg-primary text-white shadow-sm' : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {p.firstName}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <SectionLabel>How it ended</SectionLabel>
          <div className={`grid gap-2 ${compactReasons ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {reasons.map((r) => {
              const selected = endReason === r.value;
              return (
                <button
                  key={r.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => handleReasonTap(r.value)}
                  className={`min-h-[56px] rounded-2xl border flex text-sm font-bold ${
                    compactReasons ? 'flex-col items-center justify-center gap-1 py-2 px-1 text-center' : 'items-center gap-2.5 px-3 text-left'
                  } ${
                    selected
                      ? 'border-2 border-primary bg-primary/10 text-gray-900 dark:text-gray-50'
                      : 'border-gray-200 bg-gray-50 text-gray-800 active:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:active:bg-gray-700'
                  }`}
                >
                  <span className={`w-8 h-8 rounded-lg grid place-items-center text-sm font-extrabold flex-shrink-0 ${r.tone}`}>{r.short}</span>
                  <span className="leading-tight">{r.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className={`w-full min-h-[46px] rounded-xl border px-3 flex items-center justify-between text-sm font-bold ${
            expanded
              ? 'border-primary text-primary'
              : 'border-gray-200 bg-gray-50 text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100'
          }`}
        >
          <span className="flex items-center gap-1.5">
            More detail
            {!isProUser && <Lock className="w-3.5 h-3.5 text-gray-400" aria-label="Pro feature" />}
          </span>
          <span className="flex items-center gap-1 text-xs font-semibold text-gray-500 dark:text-gray-400">
            {expanded ? 'Hide' : 'Shot, error, rally, net'}
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </span>
        </button>

        {expanded && (
          <div className="space-y-3">
            <div>
              <SectionLabel>Shot</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {SHOT_TYPES.map((opt) => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    selected={shotType === opt.value}
                    onClick={() => handleProTap(() => setShotType(shotType === opt.value ? null : opt.value))}
                  />
                ))}
              </div>
            </div>
            {showErrorSpot && (
              <div>
                <SectionLabel>Error location</SectionLabel>
                <div className="flex flex-wrap gap-1.5">
                  {ERROR_SPOTS.map((opt) => (
                    <Chip
                      key={opt.value}
                      label={opt.label}
                      selected={errorSpot === opt.value}
                      onClick={() => handleProTap(() => setErrorSpot(errorSpot === opt.value ? null : opt.value))}
                    />
                  ))}
                </div>
              </div>
            )}
            <div>
              <SectionLabel>Rally length</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {RALLY_BUCKETS.map((opt) => (
                  <Chip
                    key={opt}
                    label={opt.replace('-', '–')}
                    selected={rallyBucket === opt}
                    onClick={() => handleProTap(() => setRallyBucket(rallyBucket === opt ? null : opt))}
                  />
                ))}
              </div>
            </div>
            <div>
              <SectionLabel>At the net?</SectionLabel>
              <div className="flex gap-1.5">
                {[{ value: true, label: 'Yes' }, { value: false, label: 'No' }].map((opt) => (
                  <Chip
                    key={String(opt.value)}
                    label={opt.label}
                    selected={netApproach === opt.value}
                    onClick={() => handleProTap(() => setNetApproach(netApproach === opt.value ? null : opt.value))}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Sticky so Save stays reachable when "More detail" makes the drawer scroll. */}
        <div className="sticky bottom-0 -mx-4 -mb-4 px-4 pt-2 pb-4 grid grid-cols-3 gap-2 bg-white dark:bg-gray-900">
          <button
            type="button"
            onClick={onSkip}
            className="min-h-[50px] rounded-2xl border border-gray-200 dark:border-gray-700 text-[15px] font-bold text-gray-700 dark:text-gray-300 active:bg-gray-50 dark:active:bg-gray-800"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={() => save()}
            className="col-span-2 min-h-[50px] rounded-2xl bg-primary text-white text-[15px] font-bold active:opacity-90"
          >
            Save point
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default PointDetailsSheet;
